import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { useSessao } from '@shared/auth/sessao';
import {
  INICIO_DO_MODULO, ehCliente, podeVerModulo, precisaCompletarCadastro,
  type Contexto, type Modulo, type Usuario,
} from '@shared/auth/perfil';
import { lerIntencao, rotaDaIntencao } from '@shared/lib/intencao';
import { Login } from '@login/Login';
import { LimiteErro } from '@shared/components/erro/LimiteErro';
import { DefinirAcesso } from '@login/DefinirAcesso';
import { EmailConfirmado } from '@login/EmailConfirmado';
import { PaginaCadastro } from '@cadastro/PaginaCadastro';
import {
  Carregando, NaoEncontrado, SemAcesso, SessaoIndisponivel,
} from '@shared/components/erro/Estados';

/**
 * Router raiz: um dominio, tres modulos, roteamento por perfil.
 *
 * Cada modulo entra por `lazy`, entao vira um chunk proprio: quem so usa
 * Management nao baixa o Marketplace nem o backoffice.
 *
 * `Protegido` decide O QUE DESENHAR. Nao autoriza nada -- a rota e UX, e
 * quem barra acesso e a API, em todo request. Um cliente que digitar
 * /admin recebe 403 do servidor, nao apenas uma tela vazia.
 */

const AppAdmin = lazy(() => import('@admin/App'));
const AppMarketplace = lazy(() => import('@marketplace/App'));
const AppManagement = lazy(() => import('@management/App'));
// A lamina publica entra por `lazy` como os modulos: quem usa o painel
// nao baixa a pagina de vitrine, e quem chega pela vitrine nao baixa o
// painel inteiro.
const PaginaLamina = lazy(() => import('@publico/PaginaLamina')
  .then((m) => ({ default: m.PaginaLamina })));

/** Estados que valem para qualquer rota: carregando, sem login, erro. */
function Portao({ children }: { children: ReactNode }) {
  const { carregando, precisaLogin, erro } = useSessao();
  if (carregando) return <Carregando texto="Verificando sessão…" />;
  if (precisaLogin) return <Login />;
  if (erro) return <SessaoIndisponivel erro={erro} />;
  return <>{children}</>;
}

function Protegido({ modulo, children }: { modulo: Modulo; children: ReactNode }) {
  const { usuario, contexto, carregando, precisaLogin, erro } = useSessao();

  // O <Suspense> fica FORA de qualquer retorno condicional, e o modulo
  // sempre montado -- so escondido quando nao deve aparecer.
  //
  // Com ele dentro de um `if`, todo re-render que passasse pelo estado de
  // carregamento desmontava o chunk `lazy` ja resolvido, e o React tinha
  // de recarregar o import: a interface inteira piscava ao trocar de aba.
  const conteudo =
    carregando ? <Carregando texto="Verificando sessão…" />
    : precisaLogin ? <Login />
    : erro ? <SessaoIndisponivel erro={erro} />
    // Antes de checar o modulo: um provedor sem cadastro completo nao
    // tem o que fazer no Marketplace, e "sem acesso" nao diria a ele qual
    // e o proximo passo.
    : precisaCompletarCadastro(usuario) ? <Navigate to="/cadastro" replace />
    : !podeVerModulo(usuario, modulo) ? foraDoModulo(modulo, usuario, contexto)
    : null;

  // Sem wrapper: um <div> em volta quebraria o grid de tela cheia do
  // shell. O Suspense e o unico elemento estavel, e ele nao gera DOM.
  return (
    <Suspense fallback={<Carregando />}>
      {conteudo ?? children}
    </Suspense>
  );
}

/**
 * Para onde vai quem acabou de autenticar.
 *
 * A intencao de contratar vem ANTES do modulo padrao: quem clicou em
 * "Contratar" na lamina publica e passou pelo login estava no meio de
 * uma compra, e despeja-lo no Marketplace generico faria a carteira que
 * ele estava vendo sumir -- ele teria de achar o link de novo.
 *
 * Vale so para o CLIENTE. Um provedor que por acaso tenha uma intencao
 * guardada na aba (clicou na propria lamina, por exemplo) segue para o
 * painel dele: contratar nao e um ato que lhe cabe, e o banco proibe
 * ser cliente e provedor ao mesmo tempo.
 *
 * A intencao NAO e consumida aqui -- quem a apaga e a tela de
 * confirmacao, ao montar. Limpar antes de chegar la faria um refresh no
 * meio do caminho devolver a pessoa ao Marketplace.
 */
function destinoPosLogin(usuario: Usuario | null, contexto: Contexto | null):
    string | null {
  if (ehCliente(usuario)) {
    const intencao = lerIntencao();
    if (intencao) return rotaDaIntencao(intencao);
  }
  return contexto ? INICIO_DO_MODULO[contexto.modulo] : null;
}

/**
 * Logado, mas num modulo que nao e o dele.
 *
 * O CASO COMUM E O LOGIN: o formulario aparece NO LUGAR da pagina pedida,
 * sem trocar a URL. Um gestor que abriu /marketplace (o link que um
 * cliente usa, uma aba antiga de outra sessao) entrava e continuava em
 * /marketplace -- e via "Sem acesso" logo depois de um login que deu
 * certo. Vai para onde iria entrando pela raiz.
 *
 * "Sem acesso" fica para quem nao tem modulo nenhum. A API continua
 * barrando o que nao e dele em todo request: isto so escolhe a tela.
 */
function foraDoModulo(modulo: Modulo, usuario: Usuario | null,
                      contexto: Contexto | null): ReactNode {
  const destino = destinoPosLogin(usuario, contexto);
  return destino ? <Navigate to={destino} replace /> : <SemAcesso modulo={modulo} />;
}

/** Entrada: manda para o modulo do contexto ativo, sem o usuario escolher. */
function Entrada() {
  const { contexto, usuario } = useSessao();
  const destino = destinoPosLogin(usuario, contexto);
  return (
    <Portao>
      {precisaCompletarCadastro(usuario)
        ? <Navigate to="/cadastro" replace />
        : destino
          ? <Navigate to={destino} replace />
          : <SemAcesso />}
    </Portao>
  );
}

/**
 * /entrar renderizava o formulario SEMPRE. Quem logasse por essa URL
 * ficava olhando o form de novo, ja autenticado -- parecia que o login
 * nao tinha funcionado. Com sessao valida, cai fora daqui.
 */
function PaginaEntrar() {
  const { carregando, precisaLogin, contexto, usuario } = useSessao();
  if (carregando) return <Carregando texto="Verificando sessão…" />;
  if (precisaLogin) return <Login />;
  // É ESTA a rota para onde a lâmina manda quem clica em "Contratar":
  // o destino pós-login precisa valer aqui, não só na raiz.
  if (precisaCompletarCadastro(usuario)) return <Navigate to="/cadastro" replace />;
  const destino = destinoPosLogin(usuario, contexto);
  if (destino) return <Navigate to={destino} replace />;
  return <SemAcesso />;
}

export const router = createBrowserRouter([
  { path: '/', element: <Entrada /> },
  { path: '/entrar', element: <PaginaEntrar /> },
  // Onde o convite aterrissa: o provedor ainda nao tem senha.
  { path: '/definir-acesso', element: <DefinirAcesso /> },
  // Onde o link de confirmacao do cadastro aterrissa, ja com a sessao.
  // Fora do Portao: a sessao chega pela propria URL, e o Portao mostraria
  // o login no instante antes de ela ser trocada.
  { path: '/email-confirmado', element: <EmailConfirmado /> },
  { path: '/cadastro', element: <Portao><PaginaCadastro /></Portao> },
  {
    path: '/admin/*',
    errorElement: <LimiteErro />,
    element: <Protegido modulo="admin"><AppAdmin /></Protegido>,
  },
  {
    path: '/marketplace/*',
    errorElement: <LimiteErro />,
    element: <Protegido modulo="marketplace"><AppMarketplace /></Protegido>,
  },
  {
    path: '/management/*',
    errorElement: <LimiteErro />,
    element: <Protegido modulo="management"><AppManagement /></Protegido>,
  },
  /**
   * A LAMINA PUBLICA, no nivel raiz: `/{gestor}/{slug}`.
   *
   * VEM POR ULTIMO, depois de todas as rotas do app. O React Router
   * casa na ordem, entao `/management/estrategias` e capturado pela
   * rota de management la em cima -- este padrao so recebe o que
   * sobrou. Sem essa ordem, um gestor com slug "admin" sequestraria o
   * backoffice.
   *
   * SEM `Portao`: nao ha sessao a verificar. E o unico lugar do app
   * onde isso e verdade, e por isso a rota fica solta aqui em vez de
   * dentro de um modulo.
   */
  {
    path: '/:gestor/:slug',
    errorElement: <LimiteErro />,
    element: (
      <Suspense fallback={<Carregando />}>
        <PaginaLamina />
      </Suspense>
    ),
  },
  { path: '*', element: <NaoEncontrado /> },
]);
